import { type FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { MeResponse } from "../../shared/api-types";
import { emailOnlySchema } from "../../shared/schemas";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Alert, AuthLayout, TextField } from "../components/ui";
import { apiFailure, validate } from "./forms";

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signedIn } = useAuth();
  const token = params.get("token");
  const started = useRef(false);
  const [status, setStatus] = useState<"working" | "failed">(token ? "working" : "failed");
  const [message, setMessage] = useState(
    token ? "" : "Linkul nu este complet. Deschide-l din nou din e-mail.",
  );
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string>();
  const [resent, setResent] = useState(false);

  useEffect(() => {
    // The token is single-use: guard against React StrictMode's double effect in development.
    if (!token || started.current) return;
    started.current = true;
    window.history.replaceState(null, "", "/verificare-email");
    api<MeResponse>("POST", "/auth/verify-email", { token })
      .then((me) => {
        signedIn(me);
        navigate("/app", { replace: true });
      })
      .catch((err) => {
        setStatus("failed");
        setMessage(apiFailure(err).message || "Linkul nu mai este valabil. Cere unul nou.");
      });
  }, [token, signedIn, navigate]);

  async function resend(e: FormEvent) {
    e.preventDefault();
    const v = validate(emailOnlySchema, { email });
    if (!v.ok) return setEmailError(v.errors.email);
    setEmailError(undefined);
    try {
      await api("POST", "/auth/resend-verification", v.data);
      setResent(true);
    } catch (err) {
      setMessage(apiFailure(err).message);
    }
  }

  if (status === "working") {
    return (
      <AuthLayout>
        <h1 className="h1">Confirmăm adresa…</h1>
        <p className="muted" role="status" style={{ margin: 0 }}>
          Durează doar o clipă.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="h1">Linkul nu mai este valabil</h1>
      {message && (
        <Alert tone="error" live>
          {message}
        </Alert>
      )}
      {resent ? (
        <Alert tone="info" live>
          Dacă adresa are un cont neconfirmat, ți-am trimis un link nou.
        </Alert>
      ) : (
        <form onSubmit={resend} noValidate>
          <TextField
            id="verify-email"
            label="E-mailul contului"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={emailError}
          />
          <button className="btn btn-primary btn-block" type="submit">
            Trimite un link nou
          </button>
        </form>
      )}
      <p className="auth-foot">
        <Link to="/login">Înapoi la autentificare</Link>
      </p>
    </AuthLayout>
  );
}
